/**
 * PCMStreamPlayer
 *
 * Plays raw PCM int16 mono audio streamed from the backend (Kyutai Pocket TTS).
 * Uses an AudioWorklet running on a dedicated audio thread for glitch-free playback.
 *
 * Sample rate: 24 000 Hz (Kyutai TTS native rate).
 *
 * Usage:
 *   const player = new PCMStreamPlayer(onPlayingChange);
 *   await player.start();           // call on user gesture
 *   player.push(int16ArrayBuffer);  // call for each binary WebSocket frame
 *   player.flush();                 // call on interruption / turn reset
 *   player.stop();                  // call on component unmount
 */

const SAMPLE_RATE = 24000;
// Sized for safety. Backend now paces TTS at ~1s ahead of playback (see
// voice_agent_processor.TTS_BUFFER_LEAD_S), so under normal operation only
// ~24k samples are ever in flight. The 30 s margin absorbs any momentary
// burst (e.g. WebSocket flush after pause) without overflow.
const BUFFER_SAMPLES = 720000; // 30 s at 24 kHz
const HEADER_INTS = 2;        // write cursor + read cursor
const SAB_BYTES = HEADER_INTS * 4 + BUFFER_SAMPLES * 2; // 4 bytes/int32, 2 bytes/int16

export class PCMStreamPlayer {
  private ctx: AudioContext | null = null;
  private workletNode: AudioWorkletNode | null = null;
  private sab: SharedArrayBuffer | null = null;
  private ctrl: Int32Array | null = null;
  private data: Int16Array | null = null;
  private onPlayingChange: (playing: boolean) => void;
  private _started = false;

  constructor(onPlayingChange: (playing: boolean) => void) {
    this.onPlayingChange = onPlayingChange;
  }

  /** Must be called once on user gesture (e.g. microphone button click). */
  async start(): Promise<void> {
    if (this._started) {
      if (this.ctx?.state === 'suspended') await this.ctx.resume();
      return;
    }

    if (!window.SharedArrayBuffer) {
      console.warn('[PCMStreamPlayer] SharedArrayBuffer not available — falling back to no-op (COOP/COEP headers may be missing)');
      // Graceful no-op: audio simply won't play, but the app won't crash
      return;
    }

    this.ctx = new AudioContext({ sampleRate: SAMPLE_RATE });
    if (this.ctx.state === 'suspended') await this.ctx.resume();

    await this.ctx.audioWorklet.addModule('/audio-worklets/pcm-streamer.worklet.js');

    this.sab = new SharedArrayBuffer(SAB_BYTES);
    this.ctrl = new Int32Array(this.sab, 0, HEADER_INTS);
    this.data = new Int16Array(this.sab, HEADER_INTS * 4, BUFFER_SAMPLES);

    this.workletNode = new AudioWorkletNode(this.ctx, 'pcm-streamer-processor');
    this.workletNode.port.onmessage = (ev) => {
      if (ev.data.type === 'playing') {
        this.onPlayingChange(ev.data.value as boolean);
      }
    };
    this.workletNode.port.postMessage({ type: 'init', sab: this.sab });
    this.workletNode.connect(this.ctx.destination);

    this._started = true;
    console.log('[PCMStreamPlayer] AudioWorklet started at', SAMPLE_RATE, 'Hz');
  }

  /** Push a chunk of PCM int16 samples into the ring buffer. */
  push(buffer: ArrayBuffer): void {
    if (!this.ctrl || !this.data) return;

    const samples = new Int16Array(buffer);
    const writePos = Atomics.load(this.ctrl, 0);
    const readPos = Atomics.load(this.ctrl, 1);

    // Check available space (leave 1 sample gap to distinguish full from empty)
    const used = (writePos - readPos + BUFFER_SAMPLES) % BUFFER_SAMPLES;
    const available = BUFFER_SAMPLES - 1 - used;

    if (samples.length > available) {
      // Buffer would overflow — drop this chunk to avoid corruption
      console.warn(`[PCMStreamPlayer] Buffer overflow: ${samples.length} samples, only ${available} free. Dropping chunk.`);
      return;
    }

    let pos = writePos;
    for (let i = 0; i < samples.length; i++) {
      this.data[pos] = samples[i];
      pos = (pos + 1) % BUFFER_SAMPLES;
    }

    Atomics.store(this.ctrl, 0, pos);
  }

  /** Flush the ring buffer immediately (call on barge-in / interruption). */
  flush(): void {
    if (this.workletNode) {
      this.workletNode.port.postMessage({ type: 'flush' });
    } else if (this.ctrl) {
      Atomics.store(this.ctrl, 0, 0);
      Atomics.store(this.ctrl, 1, 0);
    }
    this.onPlayingChange(false);
  }

  /** Signal that no more PCM data will arrive (call on tts_done).
   *  The worklet will signal playing:false as soon as the buffer empties. */
  drain(): void {
    if (this.workletNode) {
      this.workletNode.port.postMessage({ type: 'drain' });
    }
  }

  /** Resume if suspended (call on tab visibility change). */
  resume(): void {
    if (this.ctx?.state === 'suspended') this.ctx.resume();
  }

  /** Clean up on component unmount. */
  stop(): void {
    this.flush();
    this.workletNode?.disconnect();
    this.workletNode = null;
    if (this.ctx && this.ctx.state !== 'closed') this.ctx.close();
    this.ctx = null;
    this.sab = null;
    this.ctrl = null;
    this.data = null;
    this._started = false;
  }
}
